import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInterval } from 'react-use';

import classes from './GameList.module.scss';
import { FaPlus } from 'react-icons/fa';
import { GameConfigDTO, RoomDTO } from '@envclient/envcore/src/types/core';
import Button from './Button';
import { RootState, useAppSelector } from '../reducers/store';
import { isSupported } from '../utils/game';
import WsConnection from '../service/WsConnection';
import { useNewUser } from '../hooks/useNewUser';
import { AppStatus } from '../reducers/appStatusReducer';
import { removeInviteRoom } from '../reducers/inviteRoomsReducer';

const GameList: FC<Props> = ({ showCreate, onShowCreate }) => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const appStatus = useSelector((state: RootState) => state.appStatus);
  const gameList = useSelector((state: RootState) => state.gameList);
  const inviteRooms = useSelector((state: RootState) => state.inviteRooms);

  const flatGameConfigs = Object.values(useAppSelector((state) => state.gameConfigs)).flat();

  // tick once a second while there are pending invites, to drive the countdown / expiry
  const [now, setNow] = useState(() => Date.now());
  useInterval(() => setNow(Date.now()), inviteRooms.length ? 1000 : null);

  // resync the clock when the invite set changes so a fresh invite shows the right count immediately
  useEffect(() => {
    setNow(Date.now());
  }, [inviteRooms]);

  // drop invites once they expire so the Join button hides again
  useEffect(() => {
    inviteRooms.forEach((r) => {
      if (r.expiresAt <= now) dispatch(removeInviteRoom(r.roomId));
    });
  }, [now, inviteRooms, dispatch]);

  // seconds left on a live invite for this room, or null if none
  const inviteSecondsLeft = (roomId: number): number | null => {
    const invite = inviteRooms.find((r) => r.roomId === roomId && r.expiresAt > now);
    return invite ? Math.ceil((invite.expiresAt - now) / 1000) : null;
  };

  const handleJoin = (room: RoomDTO) => {
    if (!user) return;
    const gameRoom = gameList.find((g) => g.room.roomId === room.roomId);
    const roomGameConfig = flatGameConfigs.find((g) => g.type === gameRoom?.gameType);
    if (!isSupported(roomGameConfig, window.appConfig.version)) return;
    WsConnection.send({ type: 'JOIN', room });
    dispatch(removeInviteRoom(room.roomId));
  };

  const isNewUser = useNewUser();

  const ccsonGameTypes = ['mini', 'base', 'base|base', 'exp1', 'exp2', 'exp1|exp1', 'exp2|exp2', 'exp1|exp2'];

  const renderList = () => {
    const filteredGames =
      /* isNewUser ? gameList.filter((g) => !ccsonGameTypes.includes(g.gameType as string)) : */ gameList;
    return filteredGames.map((g) => {
      const secondsLeft = inviteSecondsLeft(g.room.roomId);
      const invited = secondsLeft !== null;
      const full = g.room.userList.length >= g.maxPlayer;
      // joinable for open rooms, or for any room you have a live invite to (even if locked),
      // but never when the room is full. invite timer keeps running (expiresAt is fixed), so if
      // a seat frees up the Join button reappears with the countdown continuing - not restarting.
      const showJoin = !full && (!g.locked || invited);
      return (
        <li className={classes.listItem} key={g.room.roomId}>
          <div className={classes.itemCont}>
            <span className={classes.roomName}>{g.room.roomName}</span>
            <span className={classes.tilesLeft}>{g.gameInfo}</span>
            {showJoin && (
              <Button onClick={() => handleJoin(g.room)} secondary>
                {invited ? `Join (${secondsLeft})` : 'Join'}
              </Button>
            )}
          </div>
          <div className={classes.users}>{g.room.userList.map((u) => u.displayName).join(', ')}</div>
        </li>
      );
    });
  };

  return (
    <div className={classes.mainCont}>
      <div className={classes.header}>
        <div className={classes.title}>Games</div>
        <Button onClick={onShowCreate} disabled={showCreate || appStatus === AppStatus.GAME}>
          <FaPlus />
          Create
        </Button>
      </div>

      <div className={classes.body}>
        <ul className={classes.list}>{renderList()}</ul>
      </div>
    </div>
  );
};

interface Props {
  showCreate: boolean;
  onShowCreate: () => void;
}

export default GameList;
